import * as tus from 'tus-js-client';
import { api } from './api.js';
import { makePhotoDerivatives, probeVideo, grabVideoPoster } from './preview.js';

/**
 * RESUMABLE UPLOAD CLIENT
 * -----------------------
 * Files go straight from this browser to R2 using presigned URLs. Nothing large
 * passes through our server.
 *
 * Small file  -> single presigned PUT.
 * Large file  -> R2 multipart: slice into parts, PUT each part, then ask the
 *                server to complete the upload.
 *
 * Resume works like this: when a multipart upload starts we record
 * {assetId, key} in localStorage under a fingerprint of the file
 * (name + size + lastModified). If the same file is dropped in again after a
 * crash, refresh, or closed laptop, we look up the assetId, ask the server
 * which parts R2 already holds, and upload only the missing ones. R2 keeps
 * incomplete multipart uploads for 7 days by default, which is the practical
 * limit on how long a resume stays possible.
 *
 * JUDGEMENT CALLS worth your input:
 *  - PART_CONCURRENCY 4: fast enough to saturate a typical 50-200Mbit upstream
 *    without holding 8 x 32MB in memory. Raise it on fibre.
 *  - FILE_CONCURRENCY 2: two files in flight keeps the pipe busy while one
 *    file finalises. Photos are small, so this mostly matters for the video.
 *  - Retries are 3 per part with exponential backoff. A part that fails four
 *    times marks the file failed but leaves the multipart upload open, so
 *    hitting "retry" resumes rather than restarts.
 */

const PART_CONCURRENCY = 4;
const MAX_PART_RETRIES = 3;
const LS_KEY = 'wg_uploads_v1';

function fingerprint(file, galleryId) {
  return `${galleryId}:${file.name}:${file.size}:${file.lastModified}`;
}

function loadResumeMap() {
  try {
    return JSON.parse(localStorage.getItem(LS_KEY) || '{}');
  } catch {
    return {};
  }
}

function rememberUpload(fp, data) {
  const map = loadResumeMap();
  map[fp] = { ...data, at: Date.now() };
  // Prune anything older than R2's 7-day incomplete-upload window.
  const cutoff = Date.now() - 7 * 24 * 60 * 60 * 1000;
  for (const [k, v] of Object.entries(map)) if (v.at < cutoff) delete map[k];
  localStorage.setItem(LS_KEY, JSON.stringify(map));
}

function forgetUpload(fp) {
  const map = loadResumeMap();
  delete map[fp];
  localStorage.setItem(LS_KEY, JSON.stringify(map));
}

/** XHR rather than fetch: fetch still has no upload progress events. */
function putWithProgress(url, body, contentType, onProgress, signal) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', url, true);
    if (contentType) xhr.setRequestHeader('Content-Type', contentType);
    xhr.upload.onprogress = (e) => onProgress?.(e.loaded);
    xhr.onload = () =>
      xhr.status >= 200 && xhr.status < 300
        ? resolve({ etag: xhr.getResponseHeader('ETag') })
        : reject(new Error(`Upload failed with status ${xhr.status}`));
    xhr.onerror = () => reject(new Error('Network error during upload'));
    xhr.onabort = () => reject(Object.assign(new Error('Cancelled'), { cancelled: true }));
    signal?.addEventListener('abort', () => xhr.abort(), { once: true });
    xhr.send(body);
  });
}

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

/**
 * Uploads one file. `onProgress(fraction)` reports 0..1 across all parts.
 * Returns the created assetId.
 */
export async function uploadFile(opts) {
  const isVideo = opts.file.type.startsWith('video/');
  return isVideo ? uploadVideo(opts) : uploadPhoto(opts);
}

/**
 * FILM -> BUNNY STREAM (resumable tus, straight from the browser).
 *
 * The server creates the Bunny video and signs the upload; the bytes go
 * browser -> Bunny, never through us. tus-js-client handles resume on its own
 * (it remembers the upload URL in localStorage), so a dropped connection or a
 * closed laptop picks up where it left off when the same file is dropped again.
 *
 * After the bytes land, Bunny transcodes the adaptive ladder asynchronously, so
 * we poll for "ready" and report it as processing — the film only appears to
 * clients once Bunny has finished encoding.
 */
async function uploadVideo({ file, galleryId, onProgress, onStatus, signal }) {
  onStatus?.('preparing');
  const meta = await probeVideo(file).catch(() => ({}));
  const poster = await grabVideoPoster(file).catch(() => null); // default thumbnail, replaceable in admin

  const init = await api.post(`/api/admin/galleries/${galleryId}/uploads`, {
    filename: file.name,
    contentType: file.type || 'video/mp4',
    size: file.size,
    kind: 'video',
    width: meta.width,
    height: meta.height,
    duration: meta.duration,
  });

  onStatus?.('uploading');
  await new Promise((resolve, reject) => {
    const upload = new tus.Upload(file, {
      endpoint: init.endpoint,
      headers: init.headers,
      metadata: init.metadata,
      // Chunked so a multi-GB film survives flaky links and proxy limits;
      // 128MB is a multiple of 256KB as Bunny requires.
      chunkSize: 128 * 1024 * 1024,
      retryDelays: [0, 2000, 5000, 10000, 20000],
      onError: reject,
      onProgress: (sent, total) => onProgress?.((sent / total) * 0.9), // reserve last 10% for encoding
      onSuccess: resolve,
    });
    signal?.addEventListener('abort', () => upload.abort().then(() => reject(Object.assign(new Error('Cancelled'), { cancelled: true }))), { once: true });
    upload.start();
  });

  // Upload the default poster to R2 (used as the film's card/hero thumbnail).
  if (poster) {
    const { url } = await api.post(`/api/admin/assets/${init.assetId}/poster-url`);
    await putWithProgress(url, poster, 'image/jpeg', null, signal).catch(() => {});
  }

  // Wait for Bunny to finish encoding before calling it done.
  onStatus?.('processing');
  for (let i = 0; i < 600; i++) {
    if (signal?.aborted) throw Object.assign(new Error('Cancelled'), { cancelled: true });
    const { status, encodeProgress } = await api.get(`/api/admin/uploads/${init.assetId}/stream-status`).catch(() => ({ status: 'processing' }));
    if (status === 'ready') break;
    if (status === 'error') throw new Error('Bunny could not process this film. Try re-uploading.');
    onProgress?.(0.9 + Math.min(0.09, ((encodeProgress || 0) / 100) * 0.09));
    await sleep(3000);
  }

  onProgress?.(1);
  onStatus?.('done');
  return init.assetId;
}

/** PHOTO -> R2 (presigned PUT, or multipart for the rare very large image). */
async function uploadPhoto({ file, galleryId, onProgress, onStatus, onWarning, signal }) {
  const fp = fingerprint(file, galleryId);
  const resumable = loadResumeMap()[fp];

  onStatus?.('preparing');
  let meta = {};
  const derivatives = {};
  const out = await makePhotoDerivatives(file).catch(() => null);
  if (out) {
    if (out.thumb) derivatives.thumb = out.thumb;
    if (out.preview) derivatives.preview = out.preview;
    meta = { width: out.width, height: out.height };
  } else {
    onWarning?.({
      level: 'info',
      title: `${file.name} has no web preview`,
      detail: 'This browser could not decode it, so the gallery will show the full-size original in the grid. It still uploads and downloads correctly, just slower to browse.',
    });
  }

  let init;
  if (resumable?.assetId) {
    onStatus?.('resuming');
    init = { ...resumable, resumed: true };
  } else {
    init = await api.post(`/api/admin/galleries/${galleryId}/uploads`, {
      filename: file.name,
      contentType: file.type || 'application/octet-stream',
      size: file.size,
      kind: 'photo',
      width: meta.width,
      height: meta.height,
    });
  }

  onStatus?.('uploading');
  let parts = null;
  if (!init.multipart) {
    await putWithProgress(init.putUrl, file, file.type, (loaded) => onProgress?.(loaded / file.size), signal);
  } else {
    rememberUpload(fp, init);
    parts = await uploadParts({ file, init, onProgress, signal });
  }

  const variants = Object.keys(derivatives);
  if (variants.length) {
    onStatus?.('finishing');
    const urls = await api.post(`/api/admin/uploads/${init.assetId}/derivative-urls`, { variants });
    await Promise.all(
      variants.map((variant) =>
        putWithProgress(urls[variant].url, derivatives[variant], 'image/jpeg', null, signal).catch(() => {
          console.warn(`${variant} upload failed for`, file.name);
        }),
      ),
    );
  }

  // Complete last so the asset only becomes visible once original AND preview
  // are both in R2 - a half-uploaded gallery never appears in the client view.
  await api.post(`/api/admin/uploads/${init.assetId}/complete`, parts ? { parts } : {});
  forgetUpload(fp);
  onProgress?.(1);
  onStatus?.('done');
  return init.assetId;
}

async function uploadParts({ file, init, onProgress, signal }) {
  const partSize = init.partSize;
  const total = Math.ceil(file.size / partSize);
  const numbers = Array.from({ length: total }, (_, i) => i + 1);

  // On resume, ask R2 which parts it already has and skip them.
  let done = new Map();
  if (init.resumed) {
    try {
      const { parts } = await api.get(`/api/admin/uploads/${init.assetId}/parts`);
      for (const p of parts) {
        // Only trust a part whose size matches what we would have sent -
        // guards against a truncated part being accepted as complete.
        const expected = p.PartNumber === total ? file.size - (total - 1) * partSize : partSize;
        if (p.Size === expected) done.set(p.PartNumber, { PartNumber: p.PartNumber, ETag: p.ETag });
      }
    } catch (err) {
      if (err.status === 409) throw new Error('This upload expired on the storage side. Remove it and upload again.');
      throw err;
    }
  }

  const pending = numbers.filter((n) => !done.has(n));
  const { urls } = pending.length
    ? await api.post(`/api/admin/uploads/${init.assetId}/part-urls`, { partNumbers: pending })
    : { urls: {} };

  const loadedPerPart = new Map([...done.keys()].map((n) => [n, partSize]));
  const report = () => {
    const sum = [...loadedPerPart.values()].reduce((a, b) => a + b, 0);
    onProgress?.(Math.min(1, sum / file.size));
  };
  report();

  let cursor = 0;
  async function worker() {
    while (cursor < pending.length) {
      if (signal?.aborted) throw Object.assign(new Error('Cancelled'), { cancelled: true });
      const n = pending[cursor++];
      const blob = file.slice((n - 1) * partSize, Math.min(n * partSize, file.size));
      let attempt = 0;
      for (;;) {
        try {
          const { etag } = await putWithProgress(
            urls[n],
            blob,
            null,
            (loaded) => {
              loadedPerPart.set(n, loaded);
              report();
            },
            signal,
          );
          done.set(n, { PartNumber: n, ETag: etag });
          loadedPerPart.set(n, blob.size);
          report();
          break;
        } catch (err) {
          if (err.cancelled) throw err;
          if (++attempt > MAX_PART_RETRIES) throw new Error(`Part ${n} failed after ${MAX_PART_RETRIES} retries`);
          await sleep(500 * 2 ** attempt);
        }
      }
    }
  }

  await Promise.all(Array.from({ length: Math.min(PART_CONCURRENCY, pending.length || 1) }, worker));

  return [...done.values()];
}
